import { HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from 'src/app/core/services/api.service';
import { environment } from 'src/environment/environment';

@Injectable({
  providedIn: 'root',
})
export class DocumentSignatureApiService extends ApiService {
  getAuthUrl$(): Observable<DocumentSignatureAuthUrlResponse> {
    return this.get('/DocumentSignature/auth-url');
  }

  isLinked$(): Observable<DocumentSignatureIsLinkedResponse> {
    return this.get('/DocumentSignature/is-linked');
  }

  prepareDocument$(
    documentId: string,
    payload: PrepareDocumentSignatureRequest,
  ): Observable<DocumentSignatureAuthUrlResponse> {
    return this.post(`/DocumentSignature/documents/${documentId}/prepare`, payload);
  }

  getSignatureSession$(documentId: string): Observable<DocumentSignatureSession | null> {
    return this.get(`/DocumentSignature/documents/${documentId}/signature-session`);
  }

  cancelSignatureSession$(
    documentId: string,
    payload: CancelDocumentSignatureSessionRequest,
  ): Observable<void> {
    return this.post(`/DocumentSignature/documents/${documentId}/cancel`, payload);
  }

  downloadSignedDocument$(documentId: string): Observable<HttpResponse<Blob>> {
    return this.getBlob(`/DocumentSignature/documents/${documentId}/download-signed`);
  }

  unlink$(): Observable<string> {
    return this.postWithResponseText(`/DocumentSignature/unlink`);
  }

  callback$(callbackParams: DocumentSignatureCallbackParams): Observable<void> {
    let params = new HttpParams();

    if (callbackParams.code) {
      params = params.set('code', callbackParams.code);
    }

    if (callbackParams.state) {
      params = params.set('state', callbackParams.state);
    }

    if (callbackParams.error) {
      params = params.set('error', callbackParams.error);
    }

    return this.get('/DocumentSignature/callback', params);
  }

  webhook$(payload: DocumentSignatureWebhookPayload): Observable<void> {
    return this.post('/DocumentSignature/webhook', payload);
  }
}

export interface DocumentSignatureAuthUrlResponse {
  url: string;
}

export interface DocumentSignatureIsLinkedResponse {
  isLinked: boolean;
}

export interface DocumentSignatureSession {
  id?: string;
  envelopeId: string;
  status: DocumentSignatureSessionStatus;
  statusChangedAt: string;
  recipients: DocumentSignatureSessionRecipient[];
}

export interface CancelDocumentSignatureSessionRequest {
  reason: string;
}

export interface DocumentSignatureSessionRecipient {
  name: string;
  email: string;
  role: DocumentSignatureRecipientRole;
  status: DocumentSignatureSessionRecipientStatus;
  statusChangedAt: string;
}

export interface DocumentSignatureCallbackParams {
  code?: string;
  state?: string;
  error?: string;
}

export interface DocumentSignatureWebhookPayload {
  event: string;
  data: DocumentSignatureWebhookPayloadData;
}

export interface DocumentSignatureWebhookPayloadData {
  accountId: string;
  userId: string;
  envelopeId: string;
}

export enum DocumentSignatureRecipientRole {
  Signer = 1,
  CarbonCopy = 2,
}

export enum DocumentSignatureSessionRecipientStatus {
  Created = 1,
  Sent = 2,
  Completed = 3,
  Declined = 4,
}

export enum DocumentSignatureSessionStatus {
  Created = 1,
  Completed = 2,
  Declined = 3,
  Voided = 4,
}

export interface PrepareDocumentSignatureRequest {
  recipients: PrepareDocumentSignatureRecipient[];
}

export interface PrepareDocumentSignatureRecipient {
  recipientId: string;
  role: DocumentSignatureRecipientRole;
}

export const apiDocumentSignatureProtectedResources = [
  {
    httpMethod: 'GET',
    scopes: [...environment.protectedResources.apiDocumentSignature.scopes.read],
  },
  {
    httpMethod: 'POST',
    scopes: [...environment.protectedResources.apiDocumentSignature.scopes.write],
  },
  {
    httpMethod: 'PUT',
    scopes: [...environment.protectedResources.apiDocumentSignature.scopes.write],
  },
  {
    httpMethod: 'DELETE',
    scopes: [...environment.protectedResources.apiDocumentSignature.scopes.write],
  },
];
